import React, { useEffect, useMemo, useRef, useState } from "react";
import { useError, useLoader, useProspect, useProspectActivities } from "@piiqtechnologies/ui-lib";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { PATHS } from "@constants/Paths";
import { messages } from "./messages.i18n";
import TabsLayout from "@layouts/tabs";
import { PeekGrid } from "@piiqtechnologies/ui-components";
import { ClockCounterClockwise, UserCircle } from "@piiqtechnologies/svg-icons";
export const ProspectIntelligenceDetailsLayout = () => {
    const [getProspect, prospect, prospectInProgress, prospectError] = useProspect();
    const [getProspectActivities, prospectActivities] = useProspectActivities();
    const { selectedCommunities, selectedOrganizations, setLayoutConfig } = useOutletContext();
    const { id } = useParams();
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(null);
    const [noOfActivity, setNoOfActivity] = useState("0");
    const firstRender = useRef(null);
    const { setError } = useError();
    const { setLoader } = useLoader();
    useEffect(() => {
        if (!prospect)
            return;
        setLayoutConfig({
            communitySelect: false,
            pageTitle: (prospect.firstName && [prospect.firstName, prospect.lastName].filter(Boolean).join(" ")) || prospect.email,
            backButton: true,
            onBack: () => navigate(PATHS.PROSPECT_INTELLIGENCE),
        });
    }, [prospect]);
    useEffect(() => {
        getProspect({ id });
    }, [id]);
    useEffect(() => {
        getProspectActivities({
            id,
            communityId: selectedCommunities,
            organizationId: selectedOrganizations,
            offset: 0,
            limit: 1,
        });
    }, [id, selectedCommunities, selectedOrganizations]);
    useEffect(() => {
        setLoader(prospectInProgress, "getProspectsInProgress");
        return () => setLoader(false, "getProspectsInProgress");
    }, [prospectInProgress]);
    useEffect(() => {
        if (prospectError) {
            setError(prospectError === null || prospectError === void 0 ? void 0 : prospectError.message);
        }
    }, [prospectError]);
    useEffect(() => {
        if (!prospectActivities)
            return;
        let timer;
        if (!firstRender.current) {
            setNoOfActivity(`${prospectActivities === null || prospectActivities === void 0 ? void 0 : prospectActivities.totalCount} Interactions`);
            // Update the display string after 30 seconds
            timer = window.setTimeout(() => {
                setNoOfActivity(`${prospectActivities === null || prospectActivities === void 0 ? void 0 : prospectActivities.totalCount}`);
            }, 3000);
            firstRender.current = true;
            return;
        }
        setNoOfActivity(`${prospectActivities === null || prospectActivities === void 0 ? void 0 : prospectActivities.totalCount}`);
        // Cleanup the timer when the component unmounts or when noOfInteraction changes
        return () => !!timer && clearTimeout(timer);
    }, [prospectActivities]);
    const PI_DETAILS_TABS = useMemo(() => [
        {
            value: PATHS.PROSPECT_INTELLIGENCE_DETAILS_SUMMARY,
            label: messages.piDetailsSummary,
            icon: React.createElement(UserCircle, { height: 17, width: 17 }),
        },
        {
            value: PATHS.PROSPECT_INTELLIGENCE_DETAILS_ACTIVITY,
            label: messages.piDetailsActivity,
            icon: React.createElement(ClockCounterClockwise, { width: 17, height: 17 }),
            labelBadgeContent: { count: noOfActivity, color: "secondary" },
        },
    ], [noOfActivity]);
    const handleTabChange = (value) => {
        navigate(value);
    };
    useEffect(() => {
        const piDetailsURLSegments = location.pathname.split("/");
        const activeTabURL = piDetailsURLSegments.pop() || piDetailsURLSegments.pop();
        const isValidTabValue = PI_DETAILS_TABS.map(({ value }) => value).includes(activeTabURL);
        setTabValue((isValidTabValue && activeTabURL) || PATHS.PROSPECT_INTELLIGENCE_DETAILS_SUMMARY);
    }, [location.pathname]);
    return (React.createElement(PeekGrid, { container: true, spacing: 2 },
        React.createElement(PeekGrid, { item: true, xs: 12 },
            React.createElement(TabsLayout, { tabs: PI_DETAILS_TABS, activeTab: tabValue, onTabChange: handleTabChange, TabAction: null, TabContent: null })),
        React.createElement(PeekGrid, { item: true, xs: 12, role: "tabpanel", id: `${tabValue}-tabPanel`, "aria-labelledby": `${tabValue}-tab` },
            React.createElement(Outlet, { context: { prospect, id } }))));
};
export default ProspectIntelligenceDetailsLayout;
